<template>
    <div class="columns">
        <div class="column is-8 is-offset-2">
            <div class="has-text-centered">
                <h2 class="mb-2 is-size-4">📦 {{ translate("page.category_manager.path_import_template_title") }}</h2>
                <p class="mb-3">{{ translate("page.category_manager.path_import_template_description") }}</p>
            </div>

            <div class="columns">
                <div class="column is-4">
                    <div class="template-selection box">
                        <h3 class="has-text-weight-bold">
                            {{ translate("page.category_manager.import_template_select_template") }}
                        </h3>
                        <p class="mb-1">{{ translate("page.category_manager.import_template_select_template_help") }}:</p>

                        <div v-if="loadingTemplates" class="is-flex is-justify-content-center py-4">
                            <LoadingSpinner2 />
                        </div>

                        <SelectionList
                            v-else
                            :multi-select="true"
                            :options="templates"
                            v-model="selectedTemplates"
                            :height="'100%'"
                            :comparing-function="(a: TemplateDescription, b: TemplateDescription) => a.id === b.id"
                        >
                            <template v-slot:option="{ option }">
                                <p>
                                    {{ translate(option.name) }}
                                </p>
                            </template>
                        </SelectionList>
                    </div>
                </div>

                <div class="column is-8">
                    <div class="template-preview box" v-if="selectedTemplates.length > 0">
                        <h3 class="has-text-weight-bold">
                            {{ translate("page.category_manager.import_template_preview_title") }}
                        </h3>
                        <p class="mb-1">{{ translate("page.category_manager.import_template_preview_help") }}:</p>
                        <CategoriesPreviewList :categories="previewedCategories" :loading="loadingPreview" :flex-height="true" />
                    </div>

                    <div class="template-placeholder box" v-else>
                        <div class="has-text-centered py-6">
                            <div class="mb-4">
                                <span class="icon is-large has-text-grey-light">
                                    <i class="is-hidden-touch fas fa-4x fa-arrow-left"></i>
                                    <i class="is-hidden-desktop fas fa-4x fa-arrow-up"></i>
                                </span>
                            </div>
                            <h3 class="has-text-weight-bold has-text-grey mb-2">
                                {{ translate("page.category_manager.import_template_no_selection_title") }}
                            </h3>
                            <p class="has-text-grey">
                                {{ translate("page.category_manager.import_template_no_selection_message") }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <hr />

            <div class="buttons is-flex is-justify-content-space-between">
                <button class="button" @click="store.navigateTo(null)">
                    <span class="icon">
                        <i class="fas fa-arrow-left"></i>
                    </span>
                    <span>{{ translate("page.category_manager.back_to_options") }}</span>
                </button>

                <button class="button is-success" :disabled="!selectedTemplates.length" @click="handleNext">
                    <span>{{ translate("page.category_manager.customize_import_button_label") }}</span>
                    <span class="icon">
                        <i class="fas fa-arrow-right"></i>
                    </span>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { translate } from "../Common/translations";
    import SelectionList from "../Common/SelectionList.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import CategoriesPreviewList from "./CategoriesPreviewList.vue";
    import { ref, onMounted, watch } from "vue";
    import { useCategoriesManagerStore } from "./categoriesManagerStore";
    import categoriesTemplatesApi, { type TemplateDescription } from "../Api/Categories/categoriesTemplates";
    import { handleError } from "../Common/errorHandler";

    const emit = defineEmits(["next"]);

    const store = useCategoriesManagerStore();

    const selectedTemplates = ref<TemplateDescription[]>([]);
    const loadingTemplates = ref(true);
    const loadingPreview = ref(false);
    const templates = ref<TemplateDescription[]>([]);
    const previewedCategories = ref<string[]>([]);

    onMounted(() => {
        return categoriesTemplatesApi
            .getAvailableTemplatesForOrganizer()
            .then(templatesFromApi => {
                templates.value = templatesFromApi.map(template => ({
                    ...template,
                }));
            })
            .catch(handleError)
            .finally(() => {
                loadingTemplates.value = false;
            });
    });

    watch(
        selectedTemplates,
        newTemplates => {
            if (newTemplates.length === 0) {
                previewedCategories.value = [];
                return;
            }

            const templateNames = newTemplates.map(t => t.id);

            loadingPreview.value = true;
            return categoriesTemplatesApi
                .previewTemplatesForOrganizer({ templateNames })
                .then(categories => {
                    previewedCategories.value = categories;
                })
                .catch(handleError)
                .finally(() => {
                    loadingPreview.value = false;
                });
        },
        { deep: true },
    );

    function handleNext() {
        emit(
            "next",
            selectedTemplates.value.map(t => ({ id: t.id, label: translate(t.name) })),
        );
    }
</script>

<style scoped>
    .template-selection,
    .template-preview,
    .template-placeholder {
        height: 55vh;
        display: flex;
        flex-direction: column;
    }

    .template-placeholder {
        justify-content: center;
    }
</style>
